import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
    RadioButtonGroupInput,
    TextInput,
    NumberInput,
    SelectInput,
    BooleanInput,
    ReferenceInput,
    AutocompleteInput,
    required,
    maxLength,
    email,
    number,
    useGetOne,
    useRecordContext,
    useTranslate,
    regex,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { WorkingDayGroupInput } from '@/components/collectionPoint/WorkingDays';
import { CouponGroupInput } from '@/components/collectionPoint/CouponGroupInput';
import { noSpecialCharacters } from '@/components/global/validators/customValidators.jsx';
import { countryChoices, operatorCouponChoices, provinceChoices } from '@/components/global/options/selectOptions';
import { OperatorPointIdInput, requiredOperators } from '@/components/collectionPoint/OperatorPointIdInput.jsx';
import { collectionPointFilterToQuery } from '@/helpers/filters/filterToQuery';
import AddressInput from '@/components/collectionPoint/AddressInput.jsx';

const CollectionPointInput = () => {
    const translate = useTranslate();
    const [newCollectionPoint, setNewCollectionPoint] = useState(false);
    const contractorId = useWatch({ name: 'contractorId' });
    const { setValue, resetField } = useFormContext();
    const record = useRecordContext();

    const defaultCollectionPointValues = {
        customId: '',
        shopLabel: '',
        shopName: '',
        operator: null,
        type: null,
        operatorPointId: '',
        serviceCompanyId: null,
        address: '',
        city: '',
        postalCode: '',
        province: null,
        country: 'Polska',
        latitude: null,
        longitude: null,
        contactName: '',
        contactEmail: '',
        contactPhoneNumber: '',
        premiumService: false,
    };

    const collectionPointFilter = contractorId ? { contractorId } : null;

    return (
        <>
            <RadioButtonGroupInput
                source='newCollectionPoint'
                label={false}
                choices={[
                    { id: 'false', name: translate('resources.collectionPoint.choices.newCollectionPoint.false') },
                    { id: 'true', name: translate('resources.collectionPoint.choices.newCollectionPoint.true') },
                ]}
                defaultValue={newCollectionPoint ? 'true' : 'false'}
                onChange={event => {
                    const creatingNew = event.target.value === 'true';
                    setNewCollectionPoint(creatingNew);
                    if (creatingNew) {
                        setValue('collectionPointId', null, { shouldDirty: true });
                        setValue('collectionPoint', defaultCollectionPointValues, { shouldDirty: true });
                        resetField('collectionPoint.coupons');
                        resetField('collectionPoint.workingHours');
                    } else {
                        setValue('collectionPointId', record?.collectionPoint?.id ?? null, { shouldDirty: true });
                        setValue('collectionPoint', {}, { shouldDirty: true });
                    }
                }}
            />
            {newCollectionPoint ? (
                <>
                    <Box display='flex'>
                        <Box flex={1}>
                            <TextInput
                                source='collectionPoint.customId'
                                label={translate('common.collectionPoint')}
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                                    noSpecialCharacters,
                                ]}
                                fullWidth
                            />
                        </Box>
                    </Box>
                    <Box display='flex'>
                        <Box flex={1} mr='1em'>
                            <TextInput
                                source='collectionPoint.shopLabel'
                                label={translate('resources.collectionPoint.fields.shopLabel')}
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(32, translate('common.validation.maxLength', { count: 32 })),
                                    noSpecialCharacters,
                                ]}
                                fullWidth
                            />
                        </Box>
                        <Box flex={1}>
                            <TextInput
                                source='collectionPoint.shopName'
                                label={translate('resources.collectionPoint.fields.shopName')}
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(32, translate('common.validation.maxLength', { count: 32 })),
                                    noSpecialCharacters,
                                ]}
                                fullWidth
                            />
                        </Box>
                    </Box>
                    <Box display='flex'>
                        <Box flex={1} mr='1em'>
                            <PosTypeInput />
                        </Box>
                        <Box flex={1}>
                            <OperatorInput />
                        </Box>
                    </Box>
                    <Box display='flex' mb='2em'>
                        <OperatorPointIdInput source='collectionPoint.operatorPointId' operatorSource='collectionPoint.operator' />
                    </Box>
                    <Typography variant='h6' gutterBottom>
                        {translate('menu.service')}
                    </Typography>
                    <Box display='flex' mb='2em'>
                        <Box flex={1} mr='1em'>
                            <ServiceCompanyInput />
                        </Box>
                    </Box>
                    <Typography variant='h6' gutterBottom>
                        {translate('resources.device.labels.coupons')}
                    </Typography>
                    <CouponGroupInput subform />
                    <Typography variant='h6' gutterBottom>
                        {translate('common.address')}
                    </Typography>
                    <Box display='flex'>
                        <Box flex={1}>
                            <AddressInput subform />
                        </Box>
                    </Box>
                    <Box display='flex'>
                        <Box flex={1} mr='1em'>
                            <TextInput
                                source='collectionPoint.address'
                                fullWidth
                                label={translate('common.street')}
                                readOnly
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                                    regex(/\d/, translate('common.validation.validAddress')),
                                ]}
                            />
                        </Box>
                        <Box flex={1}>
                            <TextInput
                                source='collectionPoint.city'
                                label={translate('common.city')}
                                fullWidth
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(30, translate('common.validation.maxLength', { count: 30 })),
                                ]}
                                readOnly
                            />
                        </Box>
                    </Box>
                    <Box display='flex'>
                        <Box flex={1} mr='1em'>
                            <TextInput
                                source='collectionPoint.postalCode'
                                fullWidth
                                label={translate('common.postalCode')}
                                validate={[
                                    required(translate('common.validation.required')),
                                    regex(/^(\d{2})-(\d{3})$/, translate('common.validation.validPostalCode')),
                                ]}
                                readOnly
                            />
                        </Box>
                        <Box flex={1}>
                            <SelectInput
                                choices={provinceChoices(translate)}
                                source='collectionPoint.province'
                                fullWidth
                                label={translate('resources.collectionPoint.form.voivodeship')}
                                validate={[required(translate('common.validation.required'))]}
                                emptyText={translate('common.all')}
                                readOnly
                            />
                        </Box>
                    </Box>
                    <Box display='flex' mb='2em'>
                        <Box flex={1}>
                            <SelectInput
                                choices={countryChoices(translate)}
                                defaultValue='Polska'
                                source='collectionPoint.country'
                                fullWidth
                                label={translate('resources.collectionPoint.form.country')}
                                validate={[required(translate('common.validation.required'))]}
                                readOnly
                            />
                        </Box>
                    </Box>
                    <Typography variant='h6' gutterBottom>
                        {translate('resources.collectionPoint.labels.coordinatesSection')}
                    </Typography>
                    <Box display='flex' mb='2em'>
                        <Box flex={1} mr='1em'>
                            <NumberInput
                                source='collectionPoint.latitude'
                                fullWidth
                                validate={number()}
                                label={translate('resources.collectionPoint.form.latitude')}
                            />
                        </Box>
                        <Box flex={1}>
                            <NumberInput
                                source='collectionPoint.longitude'
                                fullWidth
                                validate={number()}
                                label={translate('resources.collectionPoint.form.longitude')}
                            />
                        </Box>
                    </Box>
                    <Typography variant='h6' gutterBottom>
                        {translate('common.contact')}
                    </Typography>
                    <Box display='flex' mb='2em'>
                        <Box flex={1} mr='1em'>
                            <TextInput
                                source='collectionPoint.contactName'
                                fullWidth
                                label={translate('common.name')}
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(100, translate('common.validation.maxLength', { count: 100 })),
                                    noSpecialCharacters,
                                ]}
                            />
                        </Box>
                        <Box flex={1}>
                            <TextInput
                                source='collectionPoint.contactEmail'
                                fullWidth
                                label={translate('resources.contractor.fields.contactPersonEmail')}
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(100, translate('common.validation.maxLength', { count: 100 })),
                                    email(),
                                ]}
                            />
                        </Box>
                    </Box>
                    <Box display='flex' mb='2em'>
                        <Box flex={1} mr='1em'>
                            <TextInput
                                source='collectionPoint.contactPhoneNumber'
                                fullWidth
                                label={translate('resources.collectionPoint.fields.ownerPhoneNumber')}
                                validate={[
                                    required(translate('common.validation.required')),
                                    maxLength(15, translate('common.validation.maxLength', { count: 15 })),
                                ]}
                            />
                        </Box>
                    </Box>
                    <Typography variant='h6' gutterBottom>
                        {translate('resources.collectionPoint.labels.workingHours')}
                    </Typography>
                    <Box mb='2em' className='working-hours'>
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.mon')} dayShortName='mon' subform />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.tue')} dayShortName='tue' subform />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.wed')} dayShortName='wed' subform />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.thu')} dayShortName='thu' subform />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.fri')} dayShortName='fri' subform />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.sat')} dayShortName='sat' subform />
                        <WorkingDayGroupInput dayName={translate('resources.collectionPoint.days.sun')} dayShortName='sun' subform />
                    </Box>
                    <BooleanInput source='collectionPoint.premiumService' size='small' label={translate('resources.device.labels.premiumService')} fullWidth />
                </>
            ) : (
                <Box display='flex'>
                    <Box className='autocomplete-parent' flex={1} mr='1em'>
                        <ReferenceInput source='collectionPointId' reference='collection-point' filter={collectionPointFilter}>
                            <AutocompleteInput
                                filterToQuery={collectionPointFilterToQuery}
                                fullWidth
                                label={translate('common.collectionPoint')}
                                validate={required(translate('common.validation.required'))}
                                disablePortal={true}
                            />
                        </ReferenceInput>
                    </Box>
                </Box>
            )}
        </>
    );
};

const OperatorInput = () => {
    const translate = useTranslate();
    const { setValue } = useFormContext();
    const contractorId = useWatch({ name: 'contractorId' });
    const selectedCouponSource = useWatch({ name: 'collectionPoint.couponSource' });
    const { data, isLoading } = useGetOne('contractor', { id: contractorId }, { enabled: !!contractorId });
    const choices = (data?.operators ?? []).map(item => ({ id: item, name: item }));

    const handleChange = value => {
        if (selectedCouponSource) {
            const matchingCouponSourceOption = operatorCouponChoices.find(choice => choice.id === selectedCouponSource.toString().toLowerCase());
            if (matchingCouponSourceOption) {
                setValue('collectionPoint.couponSource', null);
            }
        }
        if (!requiredOperators.includes(value)) {
            setValue('collectionPoint.operatorPointId', null);
        }
    };

    return (
        <SelectInput
            source='collectionPoint.operator'
            label={translate('common.operator')}
            fullWidth={true}
            choices={choices}
            isLoading={isLoading}
            disabled={!contractorId}
            onChange={handleChange}
        />
    );
};

const PosTypeInput = () => {
    const translate = useTranslate();
    const contractorId = useWatch({ name: 'contractorId' });
    const { data, isLoading } = useGetOne('contractor', { id: contractorId }, { enabled: !!contractorId });
    const choices = (data?.posTypes ?? []).map(item => ({ id: item, name: item }));

    return (
        <SelectInput
            source='collectionPoint.type'
            label={translate('common.type')}
            fullWidth={true}
            choices={choices}
            isLoading={isLoading}
            disabled={!contractorId}
        />
    );
};

const ServiceCompanyInput = () => {
    const translate = useTranslate();
    const contractorId = useWatch({ name: 'contractorId' });
    const { setValue } = useFormContext();
    const { data, isLoading } = useGetOne(
        'contractor',
        { id: contractorId },
        {
            enabled: !!contractorId,
            onSuccess: data => {
                if (data.defaultServiceCompanyId) {
                    setValue('collectionPoint.serviceCompanyId', data.defaultServiceCompanyId, { shouldDirty: true });
                }
            },
        },
    );

    const availableCompanies = data?.serviceCompanyIds ?? [];
    const validators = availableCompanies.length > 0 ? [required(translate('common.validation.required'))] : [];

    return (
        <ReferenceInput
            source='collectionPoint.serviceCompanyId'
            reference='service-company'
            filter={{ id_in: availableCompanies }}
            enableGetChoices={({ id_in }) => id_in.length > 0}
        >
            <SelectInput
                fullWidth
                isLoading={isLoading}
                disabled={!contractorId || availableCompanies.length === 0}
                validate={validators}
                label={translate('menu.serviceCompanies')}
            />
        </ReferenceInput>
    );
};

export default CollectionPointInput;
